import { liveReload } from '@shared/services/realtime.service';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';

import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { ModulesService } from '@shared/services/modules.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { parseApiDate } from '@app/shared/utils/date-range';
import { LoggerService } from '@app/logger/logger.service';

import { InterventionsService } from '@app/shared/services/entities/interventions.service';
import { RemissionsService } from '@app/shared/services/entities/remissions.service';
import { IncidentsService } from '@app/shared/services/entities/incidents.service';
import { ShippingsService } from '@app/shared/services/entities/shippings.service';
import { ProcedureService } from '@app/shared/services/entities/procedure.service';
import { SpecialtyService } from '@app/shared/services/entities/specialty.service';
import { DoctorsService } from '@app/shared/services/entities/doctors.service';
import { PlacesService } from '@app/shared/services/entities/places.service';
import { WarehousesService } from '@app/shared/services/entities/warehouse.service';
import { incidentStatus } from '@shared/models/entities/incident.model';

import { AGENDA_KIND, AGENDA_KINDS, AgendaEvent, AgendaKind } from './agenda-types';
import { AgendaEventDialogComponent } from './event-dialog/agenda-event-dialog.component';

const log = new LoggerService('SCHEDULE COMPONENT');

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: AgendaEvent[];
}

type ViewMode = 'month' | 'week' | 'day';

/**
 * Agenda: en un solo calendario los Folios (fecha de Cx), Remisiones,
 * Incidencias y Entregas de delivery (entradas DLV) dentro del alcance
 * (hospitales) del usuario. Los indicadores filtran por tipo.
 */
@Component({
  standalone: true,
  selector: 'app-schedule',
  imports: [PageLoaderComponent, PageTitleComponent, KpiStripComponent, SearchPillComponent, CommonModule, FormsModule, MaterialModule],
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss'],
})
export class ScheduleComponent implements OnInit {
  readonly pageLabel = inject(ModulesService).moduleTitle('/schedule', 'Agenda');

  private readonly dialog = inject(MatDialog);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly userScopeService = inject(UserScopeService);
  private readonly interventionsService = inject(InterventionsService);
  private readonly remissionsService = inject(RemissionsService);
  private readonly incidentsService = inject(IncidentsService);
  private readonly shippingsService = inject(ShippingsService);
  private readonly procedureService = inject(ProcedureService);
  private readonly specialtyService = inject(SpecialtyService);
  private readonly doctorsService = inject(DoctorsService);
  private readonly placesService = inject(PlacesService);
  private readonly warehousesService = inject(WarehousesService);

  readonly kinds = AGENDA_KINDS;
  readonly kindMeta = AGENDA_KIND;
  readonly weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  readonly monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  currentView: ViewMode = 'month';
  viewDate = new Date();
  calendarDays: CalendarDay[] = [];

  /** Eventos del rango visible (todos los tipos, ya con alcance). */
  private rangeEvents: AgendaEvent[] = [];
  /** Eventos visibles tras tipo + búsqueda. */
  events: AgendaEvent[] = [];

  loading = false;
  searchText = '';
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;

  // Catálogos (nombres legibles)
  private scope: UserScope = { places: null, specialties: null };
  private procedures: Record<string, string> = {};
  private specialties: Record<string, string> = {};
  private doctors: Record<string, string> = {};
  private places: Record<string, string> = {};
  private warehouses: Record<string, { name: string; place: string }> = {};

  // Remisiones, incidencias y entradas se traen una vez y se filtran por rango
  // aquí; los folios se piden por rango al API (fecha de Cx).
  private remissions: any[] | null = null;
  private incidents: any[] | null = null;
  private deliveries: any[] | null = null;

  readonly pageLoad = new PageLoad();

  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */

  private readonly live = liveReload(['interventions', 'incidents', 'remissions', 'shippings'], () => this.pageLoad.ready && this.pageLoad.refresh(this.reload()));

  ngOnInit(): void { void this.init(); }

  init(): Promise<boolean> {
    return this.pageLoad.run(async () => {
      await this.loadCatalogs();
      await this.reload();
    });
  }

  /** Vuelve a consultar todo (botón Actualizar). */
  async refresh(): Promise<void> {
    this.remissions = this.incidents = this.deliveries = null;
    await this.pageLoad.refresh(this.reload());
  }

  // ─── Catálogos ────────────────────────────────────────────────────────────
  private async loadCatalogs(): Promise<void> {
    const cond = { searchType: 'default', condition: { deleted: false } };
    const safe = async (p: Promise<any>) => { try { return ((await p)?.data ?? []).filter((x: any) => x); } catch { return []; } };
    const [scope, procs, specs, docs, places, whs] = await Promise.all([
      this.userScopeService.load().catch(() => ({ places: null, specialties: null } as UserScope)),
      safe(this.procedureService.search(cond)),
      safe(this.specialtyService.search(cond)),
      safe(this.doctorsService.search(cond)),
      safe(this.placesService.search(cond)),
      safe(this.warehousesService.search(cond)),
    ]);
    this.scope = scope;
    const byId = (list: any[], f: (x: any) => string) => Object.fromEntries(list.map(x => [x.id, f(x)]));
    this.procedures = byId(procs, x => x.name ?? '');
    this.specialties = byId(specs, x => x.name ?? '');
    this.doctors = byId(docs, x => x.name ?? [x.firstName, x.lastName].filter(Boolean).join(' '));
    this.places = byId(places, x => x.alias || x.name || '');
    this.warehouses = Object.fromEntries(whs.map((w: any) => [w.id, { name: w.name ?? '', place: w.place ?? '' }]));
  }

  // ─── Navegación ───────────────────────────────────────────────────────────
  getCalendarTitle(): string {
    const m = this.monthNames[this.viewDate.getMonth()];
    const y = this.viewDate.getFullYear();
    if (this.currentView === 'month') return `${m} ${y}`;
    if (this.currentView === 'week') {
      const s = this.getWeekStart(this.viewDate), e = this.getWeekEnd(this.viewDate);
      return `${s.getDate()} ${this.monthNames[s.getMonth()]} – ${e.getDate()} ${this.monthNames[e.getMonth()]} ${e.getFullYear()}`;
    }
    return `${this.weekDays[this.viewDate.getDay()]} ${this.viewDate.getDate()} ${m} ${y}`;
  }

  async goToPrevious(): Promise<void> { this.shift(-1); await this.reload(); }
  async goToNext(): Promise<void> { this.shift(1); await this.reload(); }
  async goToToday(): Promise<void> { this.viewDate = new Date(); await this.reload(); }
  async changeView(view: ViewMode): Promise<void> { this.currentView = view; await this.reload(); }

  private shift(dir: number): void {
    const d = this.viewDate;
    if (this.currentView === 'month') this.viewDate = new Date(d.getFullYear(), d.getMonth() + dir, 1);
    else this.viewDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() + dir * (this.currentView === 'week' ? 7 : 1));
  }

  onDayClick(day: CalendarDay): void {
    this.viewDate = day.date;
    void this.changeView('day');
  }

  openEvent(ev: AgendaEvent): void {
    this.dialog.open(AgendaEventDialogComponent, { data: ev, width: '560px', maxWidth: '95vw' });
  }

  // ─── Rango ────────────────────────────────────────────────────────────────
  getDateRange(): { start: Date; end: Date } {
    const d = this.viewDate;
    if (this.currentView === 'month') {
      // Todo lo que se ve en la cuadrícula (6 semanas), no solo el mes.
      const first = new Date(d.getFullYear(), d.getMonth(), 1);
      const start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 41, 23, 59, 59, 999);
      return { start, end };
    }
    if (this.currentView === 'week') return { start: this.getWeekStart(d), end: this.getWeekEnd(d) };
    return {
      start: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
      end: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999),
    };
  }
  getWeekStart(date: Date): Date { return new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay()); }
  getWeekEnd(date: Date): Date { const s = this.getWeekStart(date); return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6, 23, 59, 59, 999); }
  private ymd(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

  // ─── Carga ────────────────────────────────────────────────────────────────
  async reload(): Promise<void> {
    const { start, end } = this.getDateRange();
    this.loading = true;
    try {
      const [folios] = await Promise.all([
        this.fetchFolios(start, end),
        this.remissions ? null : this.fetchAll('remissions'),
        this.incidents ? null : this.fetchAll('incidents'),
        this.deliveries ? null : this.fetchAll('deliveries'),
      ]);
      const inRange = (e: AgendaEvent) => e.date.getTime() >= start.getTime() && e.date.getTime() <= end.getTime();
      this.rangeEvents = [
        ...folios,
        ...(this.remissions ?? []).map(r => this.remissionEvent(r)),
        ...(this.incidents ?? []).map(i => this.incidentEvent(i)),
        ...(this.deliveries ?? []).map(s => this.deliveryEvent(s)),
      ].filter((e): e is AgendaEvent => !!e && !isNaN(e.date.getTime()) && inRange(e))
       .sort((a, b) => a.date.getTime() - b.date.getTime());
      this.refreshKpis();
      this.applyFilters();
    } finally {
      this.loading = false;
    }
  }

  private async fetchFolios(start: Date, end: Date): Promise<AgendaEvent[]> {
    try {
      const resp: any = await this.interventionsService.search({
        searchType: 'default',
        condition: {
          deleted: false, start: 0,
          status: ['Pendiente', 'Cancelada', 'Realizada', 'Firmado', 'T. Facturacion', 'Facturado', 'Incompleto', 'Borrador'],
          startDate: this.ymd(start), finishDate: this.ymd(end),
        },
      });
      return ((resp?.data ?? []) as any[])
        .filter(x => x)
        .filter(x => inScope(this.scope.places, x.hospital?.id ?? x.hospital) && inScope(this.scope.specialties, x.specialty))
        .map(x => this.folioEvent(x))
        .filter((e): e is AgendaEvent => !!e);
    } catch (err) {
      log.error('[fetchFolios]', err);
      await this.customMessageService.open({ title: 'Error', type: 'error', message: 'No se pudieron cargar los folios de la agenda.', dismissBtn: true });
      return [];
    }
  }

  private async fetchAll(which: 'remissions' | 'incidents' | 'deliveries'): Promise<void> {
    try {
      if (which === 'remissions') {
        const r: any = await this.remissionsService.search({ searchType: 'default', condition: { deleted: false } });
        this.remissions = ((r?.data ?? []) as any[]).filter(x => x && inScope(this.scope.places, x.place));
      } else if (which === 'incidents') {
        const r: any = await this.incidentsService.search({ searchType: 'default', condition: { deleted: false } });
        this.incidents = ((r?.data ?? []) as any[]).filter(x => x && inScope(this.scope.places, x.place));
      } else {
        const r: any = await this.shippingsService.search({ searchType: 'default', condition: { deleted: false, type: 'ENTRADA' } });
        this.deliveries = ((r?.data ?? []) as any[]).filter(x => x && inScope(this.scope.places, this.warehouses[x.toWarehouse]?.place));
      }
    } catch (err) {
      log.error(`[fetchAll ${which}]`, err);
      if (which === 'remissions') this.remissions = [];
      if (which === 'incidents') this.incidents = [];
      if (which === 'deliveries') this.deliveries = [];
    }
  }

  // ─── Eventos por tipo ─────────────────────────────────────────────────────
  private toDate(value: any): { date: Date; hasTime: boolean } | null {
    if (!value) return null;
    const ms = parseApiDate(value);
    if (isNaN(ms)) return null;
    const date = new Date(ms);
    const hasTime = /\d{1,2}:\d{2}/.test(String(value)) && !(date.getHours() === 0 && date.getMinutes() === 0);
    return { date, hasTime };
  }

  private make(kind: AgendaKind, when: { date: Date; hasTime: boolean } | null, p: Omit<AgendaEvent, 'kind' | 'date' | 'hasTime' | 'search'>): AgendaEvent | null {
    if (!when) return null;
    const search = [AGENDA_KIND[kind].singular, p.title, p.subtitle, p.place, p.status, ...p.details.map(d => d.value)]
      .join(' ').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    return { kind, date: when.date, hasTime: when.hasTime, search, ...p };
  }

  private folioEvent(x: any): AgendaEvent | null {
    const hospital = this.places[x.hospital?.id ?? x.hospital] ?? '';
    const procedure = this.procedures[x.procedure] ?? x.procedureName ?? '';
    const patient = x.patientName ?? x.patient?.name ?? '';
    const doctor = this.doctors[x.doctor] ?? '';
    const status = ['Incompleto', 'Borrador'].includes(x.status) ? 'Borrador' : (x.status ?? '');
    return this.make('FOLIO', this.toDate(x.interventionDatetime), {
      id: x.id,
      title: x.fileNumber || 'Folio sin número',
      subtitle: [procedure, patient].filter(Boolean).join(' · '),
      place: hospital,
      status,
      link: ['/interventions/wizard', x.id],
      details: [
        { label: 'Hospital', value: hospital },
        { label: 'Procedimiento', value: procedure },
        { label: 'Especialidad', value: this.specialties[x.specialty] ?? x.specialtyName ?? '' },
        { label: 'Médico', value: doctor },
        { label: 'Paciente', value: patient },
        { label: 'Episodio', value: x.episode ?? '' },
        { label: 'Horario', value: [x.startHour, x.finishHour].filter(Boolean).join(' – ') },
      ],
    });
  }

  private remissionEvent(r: any): AgendaEvent | null {
    const hospital = this.places[r.place] ?? '';
    const pieces = Array.isArray(r.items) ? r.items.length : 0;
    return this.make('REMISION', this.toDate(r.createdOn), {
      id: r.id,
      title: r.folio || 'Remisión',
      subtitle: [r.patientName, r.procedureName].filter(Boolean).join(' · ') || `${pieces} partida(s)`,
      place: hospital,
      status: r.status ?? '',
      link: ['/interventions/remission', r.id],
      details: [
        { label: 'Hospital', value: hospital },
        { label: 'Paciente', value: r.patientName ?? '' },
        { label: 'Médico', value: r.doctorName ?? '' },
        { label: 'Procedimiento', value: r.procedureName ?? '' },
        { label: 'Partidas', value: pieces ? String(pieces) : '' },
        { label: 'Total', value: r.total != null ? `$${Number(r.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '' },
      ],
    });
  }

  private incidentEvent(i: any): AgendaEvent | null {
    const hospital = this.places[i.place] ?? '';
    const piece = [i.equipmentName, i.serialNumber, i.tagRfid].filter(Boolean).join(' · ');
    return this.make('INCIDENCIA', this.toDate(i.createdOn), {
      id: i.id,
      title: i.folio || 'Incidencia',
      subtitle: [i.type, piece || i.reason].filter(Boolean).join(' · '),
      place: hospital,
      status: incidentStatus(i.status),
      link: ['/incidents'],
      details: [
        { label: 'Tipo', value: i.type ?? '' },
        { label: 'Hospital', value: hospital },
        { label: 'Pieza', value: piece },
        { label: 'Folio / remisión', value: i.interventionFolio ?? '' },
        { label: 'Motivo', value: i.reason ?? '' },
        { label: 'Prioridad', value: i.priority ?? '' },
        { label: 'Descripción', value: i.description ?? '' },
        { label: 'Reportó', value: i.reportedByName ?? '' },
      ],
    });
  }

  private deliveryEvent(s: any): AgendaEvent | null {
    const wh = this.warehouses[s.toWarehouse];
    const hospital = this.places[wh?.place ?? ''] ?? '';
    const pieces = Array.isArray(s.items) ? s.items.reduce((n: number, it: any) => n + (Number(it?.quantity) || 0), 0) : 0;
    const tagged = Array.isArray(s.rfid) ? s.rfid.length : 0;
    return this.make('DLV', this.toDate(s.receivedOn || s.createdOn), {
      id: s.id,
      title: s.deliveryFolio ? `DLV ${s.deliveryFolio}` : (s.origin ? 'Entrega – otro' : 'Entrega DLV'),
      subtitle: [wh?.name, pieces ? `${pieces} pieza(s)` : ''].filter(Boolean).join(' · '),
      place: hospital,
      status: pieces && tagged >= pieces ? 'Etiquetada' : (tagged ? `${tagged}/${pieces} etiquetadas` : 'Recibida'),
      link: ['/materialReception'],
      details: [
        { label: 'Hospital', value: hospital },
        { label: 'Locker', value: wh?.name ?? '' },
        { label: 'No. de delivery', value: s.deliveryFolio ?? '' },
        { label: 'Origen', value: s.origin ?? '' },
        { label: 'Transportista', value: s.carrierName ?? '' },
        { label: 'Piezas', value: pieces ? String(pieces) : '' },
        { label: 'Etiquetadas (RFID)', value: pieces ? `${tagged} de ${pieces}` : '' },
        { label: 'Comentarios', value: s.comments ?? '' },
      ],
    });
  }

  // ─── Filtros e indicadores ────────────────────────────────────────────────
  private refreshKpis(): void {
    const count = (k: AgendaKind) => this.rangeEvents.filter(e => e.kind === k).length;
    this.kpis = [
      { key: 'total', label: 'Todo', icon: 'calendar_month', value: this.rangeEvents.length, isTotal: true },
      ...AGENDA_KINDS.map(k => ({ key: k.kind, label: k.label, icon: k.icon, value: count(k.kind) })),
    ];
  }

  setKpi(key: string | null): void { this.activeKpi = key === 'total' ? null : key; this.applyFilters(); }
  setSearch(value: string): void { this.searchText = value ?? ''; this.applyFilters(); }

  private applyFilters(): void {
    const terms = this.searchText.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().split(/\s+/).filter(Boolean);
    this.events = this.rangeEvents.filter(e =>
      (!this.activeKpi || e.kind === this.activeKpi) && terms.every(t => e.search.includes(t)));
    this.renderCalendar();
  }

  // ─── Calendario ───────────────────────────────────────────────────────────
  private renderCalendar(): void {
    const today = new Date();
    const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const dayOf = (date: Date, isCurrentMonth: boolean): CalendarDay => ({
      date, day: date.getDate(), isCurrentMonth, isToday: sameDay(date, today),
      events: this.events.filter(e => sameDay(e.date, date)),
    });
    const { start } = this.getDateRange();
    const n = this.currentView === 'month' ? 42 : this.currentView === 'week' ? 7 : 1;
    this.calendarDays = Array.from({ length: n }, (_, i) => {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      return dayOf(date, this.currentView !== 'month' || date.getMonth() === this.viewDate.getMonth());
    });
  }

  /** Hospital · detalle (vista de día). */
  subLine(ev: AgendaEvent): string {
    return [ev.place, ev.subtitle].filter(Boolean).join(' · ');
  }

  /** Conteo por tipo de un día (vista de mes). */
  kindCounts(events: AgendaEvent[]): { kind: AgendaKind; icon: string; color: string; label: string; count: number }[] {
    return AGENDA_KINDS
      .map(k => ({ kind: k.kind, icon: k.icon, color: k.color, label: k.label, count: events.filter(e => e.kind === k.kind).length }))
      .filter(k => k.count > 0);
  }
}
